import { GenerationType, ProcessingMode, RoomType, StyleType } from 'constants/visualStaging/visualStagingTypes'
import { useCallback, useRef } from 'react'

import { VirtualStagingState } from 'models/visualStaging/virtualStagingTypes'

/**
 * Hook that provides Visual Staging actions
 * Used in the redesigned ClientPhotoStudioController flow
 */
export function useVisualStagingActions(
  currentState: VirtualStagingState,
  setState: (updater: (prev: VirtualStagingState) => VirtualStagingState) => void
) {
  // Use ref to access current state without causing re-renders
  const currentStateRef = useRef(currentState)
  currentStateRef.current = currentState

  // Memoize setState to prevent unnecessary re-renders
  const memoizedSetState = useCallback((updater: (prev: VirtualStagingState) => VirtualStagingState) => {
    return setState(updater)
  }, [setState])

  /**
   * Change generation type
   */
  const changeGenerationType = useCallback((newType: GenerationType) => {
    memoizedSetState((prev: VirtualStagingState) => {
      // When switching to PAINT_WALLS, initialize wall color to first color if not set
      // When switching away from PAINT_WALLS, clear wall color
      // When switching to CHANGE_FLOOR, initialize floor template to first template if not set
      // When switching away from CHANGE_FLOOR, clear floor template
      const updates: Partial<VirtualStagingState> = { generationType: newType }
      if (newType === GenerationType.PAINT_WALLS && !prev.wallColor) {
        updates.wallColor = 'bright_white' // Default to first color
      } else if (newType !== GenerationType.PAINT_WALLS) {
        updates.wallColor = null
      }
      if (newType === GenerationType.CHANGE_FLOOR && !prev.floorTemplate) {
        updates.floorTemplate = 'hardwood_aria' // Default to first template
      } else if (newType !== GenerationType.CHANGE_FLOOR) {
        updates.floorTemplate = null
      }

      // When switching to DECLUTTER_ROOM, clear roomType and selectedStyle (declutter doesn't need these)
      if (newType === GenerationType.DECLUTTER_ROOM) {
        updates.roomType = null
        updates.selectedStyle = null
      }

      return { ...prev, ...updates }
    })
  }, [memoizedSetState])

  /**
   * Change processing mode
   */
  const changeProcessingMode = useCallback((newMode: ProcessingMode) => {
    memoizedSetState((prev: VirtualStagingState) => ({ ...prev, processingMode: newMode }))
  }, [memoizedSetState])

  /**
   * Change room type
   */
  const changeRoomType = useCallback((newType: RoomType | null) => {
    memoizedSetState((prev: VirtualStagingState) => ({ ...prev, roomType: newType }))
  }, [memoizedSetState])

  /**
   * Change selected style
   */
  const changeSelectedStyle = useCallback((newStyle: StyleType | null) => {
    memoizedSetState((prev: VirtualStagingState) => ({ ...prev, selectedStyle: newStyle }))
  }, [memoizedSetState])

  /**
   * Toggle floor preservation
   */
  const toggleFloorPreservation = useCallback(() => {
    memoizedSetState((prev: VirtualStagingState) => ({ ...prev, keepFloorUntouched: !prev.keepFloorUntouched }))
  }, [memoizedSetState])

  /**
   * Change wall color
   */
  const changeWallColor = useCallback((color: string) => {
    memoizedSetState((prev: VirtualStagingState) => ({ ...prev, wallColor: color }))
  }, [memoizedSetState])

  /**
   * Change floor template
   */
  const changeFloorTemplate = useCallback((template: string) => {
    memoizedSetState((prev: VirtualStagingState) => ({ ...prev, floorTemplate: template }))
  }, [memoizedSetState])

  return {
    changeGenerationType,
    changeProcessingMode,
    changeRoomType,
    changeSelectedStyle,
    toggleFloorPreservation,
    changeWallColor,
    changeFloorTemplate,
  }
}
